import React, { useRef, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { FormStepContentProps } from 'types';
import { useScrollTop } from 'hooks/useScrollTop';
import { FormDataType, useFormState } from 'contexts/FormProvider';
import validationSchema, { contactDetailSchema, insuredDetailSchema } from './schema';
import { Form, FormSpacer } from 'components';
import CustomerDetails from './components/CustomerDetails';
import ContactDetails from './components/ContactDetails';
import OtherDetails from './components/OtherDetails';
import { useContentContext } from '@qbe/dfc-content';
import { Button } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { CustomerType } from './enum';
import get from 'lodash/get';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';
import { formatNext, TrackEvent } from 'hooks/useAnalytics';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIsFieldRequired } from 'utils/isFieldRequired';
import { useContentComplaintPartners } from 'utils/content';

const InsuredlDetailsForm = ({ defaultValues, onComplete, onStart }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();

  const { formData } = useFormState();

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const form = useForm<FormDataType>({
    mode: 'onBlur',
    defaultValues,
    resolver: yupResolver(validationSchema) as any,
  });

  const { isFieldRequired } = useIsFieldRequired(form, validationSchema);

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    setValue,
    getValues,
    register,
    formState: { isDirty },
  } = form;

  useEffect(() => {
    isDirty && onStart();
  }, [isDirty, onStart]);

  const values = watch();
  const isValid = validationSchema.isValidSync(values);

  const insuredIndOrBus = get(values, 'insuredIndOrBus', null);

  const [showDetailsNextButton, setShowDetailsNextButton] = useState(false);
  const [showIContactNextButton, setShowIContactNextButton] = useState(false);
  const [iContactDetails, setIContactDetails] = useState(false);
  const [otherDetails, setOtherDetails] = useState(false);

  const isCustomerDetailNextButtonEnabled = insuredDetailSchema.isValidSync(watch());
  const formRef = useRef<HTMLFormElement>(null);
  // const pContactType = control.getValues('contactType');

  // Container for info box
  // const formRef = useRef(null);

  /* useEffect(() => {
    if (pContactType) {
      setValue('contactType', pContactType);
    }
  }, [pContactType, setValue]);

  useEffect(() => {
    register('contactType');
  }, [register]);

  */

  const canSubmitContact = contactDetailSchema.isValidSync(watch());

  const onSubmit = (data: Partial<FormDataType>) => {
    let newData = data;
    if (newData.insuredIndOrBus === CustomerType.BUSINESS) {
      newData = {
        ...newData,
        customerFirstName: null,
        customerLastName: null,
        customerTitle: null,
        dob: null,
        gender: null,
      };
    }

    if (newData.insuredIndOrBus === CustomerType.INDIVIDUAL) {
      newData = {
        ...newData,
        contactFirstName: null,
        contactLastName: null,
        contactPersonTitle: null,
        businessName: null,
        isSmallBusiness: null,
      };
    }

    if (newData.addressType === 'yes') {
      newData = {
        ...newData,
        cPostalAddress: null,
        isManualAddressPhy: false,
        cPostalAddressObject: null,
        pStreetAddress: '',
        pSuburb: '',
        pCountry: '',
        pState: '',
        pPostcode: '',
      };
    }

    if (newData.isVulnerableCustomer === 'no') {
      newData = {
        ...newData,
        vulnerabilityReason: null,
        isLanguageInterpreter: null,
        language: null,
        otherLanguage: null,
      };
    }

    if (newData.vulnerabilityReason !== 'Language barriers') {
      newData = {
        ...newData,
        isLanguageInterpreter: null,
        language: null,
        otherLanguage: null,
      };
    }

    if (newData.isLanguageInterpreter === 'no') {
      newData = {
        ...newData,
        language: null,
        otherLanguage: null,
      };
    }

    if (newData.language !== 'Other') {
      newData = {
        ...newData,
        otherLanguage: null,
      };
    }

    onComplete({ ...formData, ...newData });
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} ref={formRef} data-test="insured-details-form">
      <FormCard title={getTextContent('insured.form.heading')} description="" variant="pageTitle" />
      <CustomerDetails form={form} isFieldRequired={isFieldRequired} customerType={insuredIndOrBus}>
        {!showIContactNextButton && (
          <>
            <FormSpacer type="formgroup" />

            <Button
              type="button"
              label="Next"
              onClick={() => {
                setShowIContactNextButton(true);
                setIContactDetails(true);
                trigger('iContactPreferedMethod');
                TrackEvent(formatNext('nextShowContact'), getValues());
              }}
              disabled={!isCustomerDetailNextButtonEnabled}
            />
          </>
        )}
      </CustomerDetails>

      {showIContactNextButton && (
        <ContactDetails
          form={form}
          formRef={formRef}
          contactType={get(defaultValues, 'contactType', null) as string}
          isFieldRequired={isFieldRequired}
        >
          {!showDetailsNextButton && iContactDetails && (
            <>
              <FormSpacer type="formgroup" />
              <Button
                type="button"
                label="Next"
                onClick={() => {
                  setShowDetailsNextButton(true);
                  setOtherDetails(true);
                  TrackEvent(formatNext('nextOtherDetails'), getValues());
                }}
                disabled={!canSubmitContact}
              />
            </>
          )}
        </ContactDetails>
      )}

      {showDetailsNextButton && (
        <OtherDetails form={form} isFieldRequired={isFieldRequired}>
          <FormSpacer type="formgroup" />
          {otherDetails && <Button type="submit" label="Continue" disabled={!isValid} data-track="Continue" />}
        </OtherDetails>
      )}
    </Form>
  );
};

export default InsuredlDetailsForm;
